import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "../api/api";
import { hasAdminAccess } from "../utils/roleUtils";
import { useFeatureVisibility } from "../contexts/featureVisibility.context";
import { Spin } from "antd";
import FeatureUnavailable from "../pages/FeatureUnavailable/featureUnavailable.page";

// Функция для получения ключа фичи из пути роута
const getFeatureKeyFromPath = (pathname: string): string | null => {
  const cleanPath = pathname.replace(/^\/|\/$/g, "");
  
  // Маппинг путей на ключи фич
  const pathToFeatureKey: { [key: string]: string } = {
    "": "home",
    "machines": "machines",
    "materials": "materials",
    "produces": "produces",
    "production": "production",
    "clients": "clients",
    "orders": "orders",
    "task-orders": "task-orders",
    "tasks": "tasks",
    "cashboxes": "cashboxes",
    "cash-transfers": "cash-transfers",
    "salary": "salary",
    "salary-request": "salary-request",
    "expenses": "expenses",
    "profit-credits-expenses": "profit-expenses",
    "reports": "reports",
    "audit": "audit",
    "audit-actions": "audit-actions",
    "action-log": "action-log",
    "transactions": "transactions",
    "delivery-expenses": "delivery-expenses",
    "forks": "forks",
    "inventory": "inventory",
    "inventory-logs": "inventory-logs",
    "residue": "residue",
    "price": "price",
    "staff": "staff",
    "workers": "workers",
    "positions": "positions",
    "vat": "vat",
    "vat/clients": "vat-clients",
    "vat/transactions": "vat-transactions",
    "vat/reports": "vat-reports",
    "crm": "crm",
    "crm/sales-users": "crm-sales-users",
    "commission-request": "commission-request",
    "lists": "lists",
    "files": "files",
    "meter-readings": "meter-readings",
    "fillers": "fillers",
    "settings": "settings",
    "settings/global": "settings-global",
    "settings/company": "settings-company",
    "settings/order": "settings-order",
    "settings/report": "settings-report",
    "settings/turnover": "settings-turnover",
    "settings/columns": "settings",
    "settings/features": "settings",
    "archive": "archive",
    "profile": "profile",
  };
  
  // Проверяем точное совпадение
  if (pathToFeatureKey[cleanPath]) {
    return pathToFeatureKey[cleanPath];
  }
  
  // Проверяем частичные совпадения (для вложенных путей)
  for (const [pathPattern, featureKey] of Object.entries(pathToFeatureKey)) {
    if (cleanPath.startsWith(pathPattern)) {
      return featureKey;
    }
  }
  
  // Для путей с параметрами проверяем первую часть
  const firstPart = cleanPath.split("/")[0];
  if (pathToFeatureKey[firstPart]) {
    return pathToFeatureKey[firstPart];
  }
  
  return null;
};

const PrivateRoutes = () => {
  const token = localStorage.getItem("token");
  const location = useLocation();
  const { isFeatureEnabled, loading: featuresLoading } = useFeatureVisibility();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [unavailableFeature, setUnavailableFeature] = useState<string | null>(null);

  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const response = await api.get("/settings/maintenance-mode");
        const maintenanceMode = response.data.maintenanceMode || false;
        setIsMaintenanceMode(maintenanceMode);

        // Если режим обслуживания включен и пользователь не админ
        if (maintenanceMode && token) {
          const authState = localStorage.getItem("authState");
          if (authState) {
            try {
              const parsed = JSON.parse(authState);
              const isAdmin = hasAdminAccess(parsed.user?.role);
              if (!isAdmin && window.location.pathname !== "/maintenance") {
                window.location.href = "/maintenance";
                return;
              }
            } catch (e) {
              // Если не удалось распарсить, редиректим
              if (window.location.pathname !== "/maintenance") {
                window.location.href = "/maintenance";
                return;
              }
            }
          } else {
            // Нет данных о пользователе, редиректим
            if (window.location.pathname !== "/maintenance") {
              window.location.href = "/maintenance";
              return;
            }
          }
        }
      } catch (error) {
        // Если ошибка при проверке, продолжаем работу
        console.error("Ошибка при проверке режима обслуживания:", error);
      } finally {
        setIsChecking(false);
      }
    };

    if (token) {
      checkMaintenanceMode();
    } else {
      setIsChecking(false);
    }
  }, [token]);

  // Проверка доступа к фиче
  useEffect(() => {
    if (featuresLoading || isChecking) {
      setUnavailableFeature(null);
      return;
    }
    
    const currentFeature = getFeatureKeyFromPath(location.pathname);
    if (currentFeature) {
      const authState = localStorage.getItem("authState");
      if (authState) {
        try {
          const parsed = JSON.parse(authState);
          const isAdmin = hasAdminAccess(parsed.user?.role);
          
          // Админы всегда имеют доступ
          if (!isAdmin && !isFeatureEnabled(currentFeature)) {
            setUnavailableFeature(currentFeature);
          } else {
            setUnavailableFeature(null);
          }
        } catch (e) {
          // Игнорируем ошибки парсинга
          setUnavailableFeature(null);
        }
      } else {
        setUnavailableFeature(null);
      }
    } else {
      setUnavailableFeature(null);
    }
  }, [location.pathname, isFeatureEnabled, featuresLoading, isChecking]);

  if (isChecking || featuresLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100vw",
          position: "fixed",
          top: 0,
          left: 0,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          zIndex: 9999,
        }}
      >
        <Spin size="large" tip="Загрузка..." />
      </div>
    );
  }

  if (!token) {
    return <Navigate to="login" />;
  }

  // Если режим обслуживания включен и пользователь не админ, редирект уже произошел
  if (isMaintenanceMode) {
    const authState = localStorage.getItem("authState");
    if (authState) {
      try {
        const parsed = JSON.parse(authState);
        const isAdmin = hasAdminAccess(parsed.user?.role);
        if (!isAdmin) {
          return <Navigate to="/maintenance" />;
        }
      } catch (e) {
        return <Navigate to="/maintenance" />;
      }
    } else {
      return <Navigate to="/maintenance" />;
    }
  }

  // Если раздел недоступен, показываем страницу "Раздел недоступен"
  if (unavailableFeature) {
    return <FeatureUnavailable />;
  }

  return <Outlet />;
};

export default PrivateRoutes;
